<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'next/input/Input.vue';

const { t } = useI18n();
const minutes = ref(5);
const isEnabled = ref(false);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const { auto_reassign_offline_agents, auto_reassign_offline_after } =
      currentAccount.value?.settings || {};

    isEnabled.value = auto_reassign_offline_agents === true;
    minutes.value = auto_reassign_offline_after || 5;
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (minutes.value < 1 || minutes.value > 1440) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.ERROR'));
    return Promise.resolve();
  }

  return updateAccountSettings({
    auto_reassign_offline_agents: true,
    auto_reassign_offline_after: minutes.value,
  });
};

const handleDisable = async () => {
  return updateAccountSettings({
    auto_reassign_offline_agents: false,
    auto_reassign_offline_after: null,
  });
};

const toggleAutoReassign = async () => {
  if (!isEnabled.value) handleDisable();
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.NOTE')"
    :hide-content="!isEnabled"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch v-model="isEnabled" @change="toggleAutoReassign" />
      </div>
    </template>

    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.MINUTES.LABEL')"
        :help-message="
          t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.MINUTES.HELP')
        "
      >
        <Input
          v-model.number="minutes"
          type="number"
          min="1"
          max="1440"
          class="w-full"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.MINUTES.PLACEHOLDER')
          "
        />
      </WithLabel>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="
            t('GENERAL_SETTINGS.FORM.AUTO_REASSIGN_OFFLINE.UPDATE_BUTTON')
          "
        />
      </div>
    </form>
  </SectionLayout>
</template>
